import { debugWarn, debugError } from "../common/utils/debug";

/**
 * Interview Service
 * Handles interview validation and management
 *
 * Interview link validation is now handled by the candidate backend API.
 * The backend performs comprehensive validation against both HR Portal database
 * and interview session state before allowing the interview to start.
 */

// Get backend API URL from environment
const BACKEND_API_URL = 
  process.env.REACT_APP_SERVER_URL || 
  'http://localhost:5294';

// Interview status enum
export enum InterviewStatus {
  Pending = 'Pending',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Expired = 'Expired',
  Cancelled = 'Cancelled',
  NotFound = 'NotFound',
}

export interface InterviewValidationResult {
  isValid: boolean;
  status: string;
  message?: string;
  candidateName?: string;
  canResume?: boolean;
}

/**
 * Validates an interview link via the candidate backend API.
 * Backend checks: existence, expiry, completion status, and resume capability.
 * @param interviewId - The interview ID to validate
 * @returns Promise with validation result including candidate name if valid
 */
export const verifyInterview = async (interviewId: string): Promise<InterviewValidationResult> => {
  try {
    const response = await fetch(`${BACKEND_API_URL}/api/interview/${interviewId}/validate`);
    
    if (!response.ok) {
      debugError('Failed to validate interview:', response.statusText);
      return {
        isValid: false,
        status: 'Error',
        message: 'Failed to validate interview link. Please try again.',
      };
    }

    const result = await response.json();
    return result;
  } catch (error) {
    debugError('Error validating interview:', error);
    return {
      isValid: false,
      status: 'Error',
      message: 'Unable to connect to the interview service. Please check your connection and try again.',
    };
  }
};

/**
 * DEPRECATED: Use verifyInterview instead which now returns candidate details.
 */
export const getInterviewDetails = async (interviewId: string) => {
  debugWarn('getInterviewDetails is deprecated. Use verifyInterview which returns candidateName.');
  
  const result = await verifyInterview(interviewId);
  return {
    interviewId,
    candidateName: result.candidateName || '',
  };
};

export const interviewService = {
  verifyInterview,
  getInterviewDetails,
};
